// Configuration du système de points et niveaux de la Fan Zone EFAFC

// Multiplicateurs de difficulté
export const DIFFICULTY_MULTIPLIERS = {
  facile: 1,
  moyen: 1.5,
  difficile: 2
};

// Configuration des jeux
export const GAMES_CONFIG = {
  pronostics: {
    id: 'pronostics',
    name: 'Pronostics',
    icon: '⚽',
    description: 'Prédis le score du prochain match',
    frequency: 'match',
    hasDifficulty: false,
    points: {
      scoreExact: 100,
      bonEcart: 50,
      bonResultat: 30,
      echec: 0
    },
    bonus: {
      consecutifs: 50, // 3 pronostics exacts consécutifs
    }
  },
  quiEstCe: {
    id: 'quiEstCe',
    name: 'Qui est-ce ?',
    icon: '🔍',
    description: 'Devine le joueur mystère',
    frequency: 'daily',
    hasDifficulty: true,
    maxIndices: 5,
    pointsByIndices: {
      0: 50, // Direct sans indice
      1: 40,
      2: 30,
      3: 20,
      4: 10,
      5: 5,
    },
    difficulties: {
      facile: { label: 'Facile', description: 'Joueurs titulaires actuels', pool: 'titulaires' },
      moyen: { label: 'Moyen', description: 'Tout l\'effectif actuel', pool: 'effectif' },
      difficile: { label: 'Difficile', description: 'Effectif + anciens joueurs', pool: 'all' }
    }
  },
  wordle: {
    id: 'wordle',
    name: 'Wordle EFAFC',
    icon: '📝',
    description: 'Trouve le nom du joueur en 6 essais',
    frequency: 'daily',
    hasDifficulty: true,
    maxAttempts: 6,
    pointsByAttempt: {
      1: 60,
      2: 50,
      3: 40,
      4: 30,
      5: 20,
      6: 10,
    },
    difficulties: {
      facile: { label: 'Facile', description: 'Noms de 4-6 lettres', minLength: 4, maxLength: 6, pool: 'actuels' },
      moyen: { label: 'Moyen', description: 'Noms de 5-8 lettres', minLength: 5, maxLength: 8, pool: 'effectif' },
      difficile: { label: 'Difficile', description: 'Noms de 6-10 lettres', minLength: 6, maxLength: 10, pool: 'staff' }
    }
  },
  puzzle: {
    id: 'puzzle',
    name: 'Puzzle',
    icon: '🧩',
    description: 'Reconstitue l\'image du club',
    frequency: 'daily',
    hasDifficulty: true,
    basePoints: 75,
    difficulties: {
      facile: { label: 'Facile', grid: 3, pieces: 9, timeLimit: 120 }, // 2 min
      moyen: { label: 'Moyen', grid: 4, pieces: 16, timeLimit: 180 }, // 3 min
      difficile: { label: 'Difficile', grid: 5, pieces: 25, timeLimit: 300 } // 5 min
    }
  }
};

// Système de niveaux
export const LEVELS_CONFIG = [
  { level: 1, xpRequired: 0, title: 'Nouveau Fan', badge: null, color: '#9CA3AF' },
  { level: 2, xpRequired: 500, title: 'Supporter', badge: 'bronze', color: '#CD7F32' },
  { level: 3, xpRequired: 1500, title: 'Supporter Fidèle', badge: 'argent', color: '#C0C0C0' },
  { level: 4, xpRequired: 3500, title: 'Ultra', badge: 'or', color: '#FFD700' },
  { level: 5, xpRequired: 7000, title: 'Légende', badge: 'diamant', color: '#B9F2FF' },
  { level: 6, xpRequired: 15000, title: 'Légende Immortelle', badge: 'platine', color: '#E5E4E2' },
];

// Calculer le niveau à partir des XP
export const calculateLevel = (totalXP) => {
  let currentLevel = LEVELS_CONFIG[0];
  
  for (const level of LEVELS_CONFIG) {
    if (totalXP >= level.xpRequired) {
      currentLevel = level;
    } else {
      break;
    }
  }
  
  // Calculer la progression vers le prochain niveau
  const currentLevelIndex = LEVELS_CONFIG.findIndex(l => l.level === currentLevel.level);
  const nextLevel = LEVELS_CONFIG[currentLevelIndex + 1];
  
  let progressToNext = 100;
  let xpForNext = 0;
  
  if (nextLevel) {
    const xpInCurrentLevel = totalXP - currentLevel.xpRequired;
    const xpNeededForNext = nextLevel.xpRequired - currentLevel.xpRequired;
    progressToNext = Math.min(100, Math.round((xpInCurrentLevel / xpNeededForNext) * 100));
    xpForNext = nextLevel.xpRequired - totalXP;
  }
  
  return {
    ...currentLevel,
    totalXP,
    progressToNext,
    xpForNext,
    nextLevel: nextLevel || null
  };
};

// Calculer les points pour chaque jeu
export const calculatePoints = {
  pronostics: (result) => {
    const config = GAMES_CONFIG.pronostics.points;
    switch (result) {
      case 'exact': return config.scoreExact;
      case 'ecart': return config.bonEcart;
      case 'resultat': return config.bonResultat;
      default: return config.echec;
    }
  },
  
  quiEstCe: (indicesUsed, difficulty) => {
    const basePoints = GAMES_CONFIG.quiEstCe.pointsByIndices[indicesUsed] || 0;
    const multiplier = DIFFICULTY_MULTIPLIERS[difficulty] || 1;
    return Math.round(basePoints * multiplier);
  },
  
  wordle: (attempts, difficulty) => {
    const basePoints = GAMES_CONFIG.wordle.pointsByAttempt[attempts] || 0;
    const multiplier = DIFFICULTY_MULTIPLIERS[difficulty] || 1;
    return Math.round(basePoints * multiplier);
  },
  
  puzzle: (timeRemaining, timeLimit, difficulty) => {
    const basePoints = GAMES_CONFIG.puzzle.basePoints;
    const multiplier = DIFFICULTY_MULTIPLIERS[difficulty] || 1;
    const timeRatio = timeRemaining / timeLimit;
    
    // Minimum 10 points si terminé à temps
    const points = Math.max(10, Math.round(basePoints * timeRatio));
    return Math.round(points * multiplier);
  }
};

// Format de date pour les défis quotidiens (YYYY-MM-DD)
export const getTodayDateKey = () => {
  const today = new Date();
  return today.toISOString().split('T')[0];
};
